/**
 * @file Homepage Hero Anniversary.
 */

import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';

/**
 * Homepage Hero Anniversary component.
 *
 * The following custom events are dispatched:
 * vHpMutePlayer - Implementation-agnostic trigger to mute video.
 * vHpUnmutePlayer - Implementation-agnostic trigger to unmute video.
 * vHpPausePlayer - Implementation-agnostic trigger to pause video.
 */
const vHpHeroAnniversary = () => {
	const hpHero = document.querySelector('.v-hp-hero-anniversary');

	if (!hpHero) {
		return;
	}

	gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, DrawSVGPlugin, MorphSVGPlugin);

	const mm = gsap.matchMedia();
	const bg = hpHero.querySelector('.v-hp-hero-anniversary__bg');
	const box = hpHero.querySelector('.v-hp-hero-anniversary__box');
	const video = hpHero.querySelector('.v-hp-hero-anniversary__video-inner');
	const frame = hpHero.querySelector('.v-hp-hero-anniversary__frame-inner');
	const frameSpacer = hpHero.querySelector('.v-hp-hero-anniversary__frame-spacer');
	const iframe = hpHero.querySelector('.v-hp-hero-anniversary__frame');
	const boxPlay = hpHero.querySelector('.v-hp-hero-anniversary__play-button--box');
	const framePlay = hpHero.querySelector('.v-hp-hero-anniversary__play-button--frame');
	const playIcon = hpHero.querySelector('.v-hp-hero-anniversary__play-icon');
	const videoWrap = hpHero.querySelector('.v-hp-hero-anniversary__video-wrap');
	let init = false;
	let player = null;
	let playing = false;

	/**
	 * Play box animation.
	 */
	const playBoxAnimation = () => {
		MorphSVGPlugin.convertToPath('.v-hp-hero-anniversary__tagline circle');
		const tagline = box.querySelector('.v-hp-hero-anniversary__tagline');
		const zero = tagline.querySelector('.v-tagline__zero');
		const dot = tagline.querySelector('.v-tagline__dot');
		const text = tagline.querySelector('.v-tagline__text');
		const drawable = tagline.querySelector('.v-tagline__drawable');
		const twenty = tagline.querySelector('#v-tagline__twenty');
		const boxTl = gsap.timeline({
			delay: 4,
			defaults: {
				ease: 'power1.inOut',
				duration: .5,
			},
			onStart: () => {
				tagline.classList.add('animating');
			},
			onComplete: () => {
				tagline.classList.remove('animating');
			},
		});

		boxTl
			.addLabel('start')
			.to(zero, {
				duration: .4,
				morphSVG:'#v-tagline__dot',
				onComplete: () => {
					gsap.set(zero, { opacity: 0 });
					gsap.set(dot, { opacity: 1 });
				},
			})
			.from(drawable, {
				ease: 'power2.inOut',
				drawSVG: '0%',
			}, 'start+=.2')
			.to(twenty, {opacity: 0}, 'start+=.2')
			// .to(boxPlay, {
			// 	opacity: 1,
			// 	onStart: () => {
			// 		boxPlay.classList.remove('disabled');
			// 	},
			// }, 'start+=.2')
			.from(text, {
				duration: 1,
				scale: .9,
				opacity: 0,
				ease: 'power4.inOut'
			}, 'start+=.2');
	};

	/**
	 * Interrupt video.
	 * Fades out video, mutes vdieo player, reveals bg video.
	 */
	const interruptVideo = () => {
		if (!playing) {
			return;
		}

		const tl = gsap.timeline({
			defaults: {
				ease: 'ease.out',
				duration: 0.25,
			},
			onStart: () => {
				document.dispatchEvent(new CustomEvent('vHpMutePlayer'));
			},
			onComplete: () => {
				frame.querySelector('iframe').classList.remove('show');
				hpHero.classList.remove('v-hp-hero-anniversary--playing');
				gsap.set(iframe, { clearProps: 'opacity' });
				gsap.set(videoWrap, { clearProps: 'opacity' });
			},
		});

		tl.to(iframe, { opacity: 0 }).to(videoWrap, { opacity: 1 }, '<');
	};

	/**
	 * Pause video
	 */
	const pauseVideo = () => {
		if (playing && !frame.querySelector('iframe').classList.contains('show')) {
			document.dispatchEvent(new CustomEvent('vHpPausePlayer'));
		}
	};

	/**
		 * Initialize Vimeo player.
		 */
	const initVimeoPlayer = async () => {
		const { default: Player } = await import('@vimeo/player');

		player = new Player(iframe, {
			color: '#31c3e4',
			id: iframe.getAttribute('data-id'),
			playsinline: 0
		});

		player.on('play', () => {
			playing = true;
		});

		player.on('ended', () => {
			if (!videoWrap.classList.contains('hidden')) {
				interruptVideo();
			}

			playing = false;
		});

		player.ready().then(() => {
			init = true;
			document.dispatchEvent(new CustomEvent('vHpPlayerReady'));
		});
	};

	// Init video based on source (open to expansion with other sources).
	if (iframe.getAttribute('data-source') === 'vimeo') {
		// init when video is in view
		ScrollTrigger.create({
			trigger: frame,
			start: 'top bottom-=100',
			end: 'center center',
			onEnter: () => {
				if (init) {
					return;
				}

				initVimeoPlayer();
			},
		});

		document.addEventListener('vHpMutePlayer', async () => {
			const ended = await player.getEnded();

			if (ended) {
				return;
			}

			player?.setMuted(true);
		});

		document.addEventListener('vHpUnmutePlayer', () => {
			if (!hpHero.classList.contains('v-hp-hero-anniversary--playing')) {
				return;
			}

			player?.setMuted(false);
		});

		document.addEventListener('vHpPausePlayer', async () => {
			await player?.pause();

			if (player?.getMuted()) {
				player?.setMuted(false)
			}

			playing = false;
		});
	}

	/**
	 * Gsap context with video animation
	 * utilizing matchMedia, and independent
	 * scroll triggers for pinning and player actions.
	 */
	const gsapCtx = gsap.context((context) => {
		mm.add({
			isXlarge: '(min-width: 1440px)',
			isLarge: '(min-width: 1024px) and (max-width: 1439px)',
			isMlarge: '(min-width: 769px) and (max-width: 1023px)',
			// The following are here if we need them later
			isMedium: '(min-width: 550px) and (max-width: 768px)',
			isSmall: '(max-width: 549px)',
		}, (ctx) => {
			let { isXlarge, isLarge, isMlarge } = ctx.conditions;
			const getXPerc = (x, w) => x / w * 100;
			const getYPerc = (y, h) => y / h * 100;
			const isDesktop = isLarge || isXlarge || isMlarge;

			// Video scale animation.
			const videoScaleTl = gsap.timeline({
				scrollTrigger: {
					trigger: box,
					endTrigger: frame,
					start: isDesktop ? 'top-=20% top' : 'center center',
					end: isDesktop ? 'center center' : 'center center',
					scrub: true,
					invalidateOnRefresh: true,
				},
			})
			// .to(videoWrap, {
			// 	clipPath: () => {
			// 		const {left: frameLeft, right: frameRight, height: frameHeight} = iframe.getBoundingClientRect();
			// 		const {width: bgWidth, height: bgHeight} = bg.getBoundingClientRect();
			// 		const yTop = (bgHeight - frameHeight) / 2;
			// 		const yBottom = bgHeight - yTop;

			// 		return `polygon(${getXPerc(frameLeft, bgWidth)}% ${getYPerc(yTop, bgHeight)}%, ${getXPerc(frameRight, bgWidth)}% ${getYPerc(yTop, bgHeight)}%, ${getXPerc(frameRight, bgWidth)}% ${getYPerc(yBottom, bgHeight)}%, ${getXPerc(frameLeft, bgWidth)}% ${getYPerc(yBottom, bgHeight)}%)`;
			// 	},
			// })
			// .to(video, {
			// 	scale: () => frame.getBoundingClientRect().width / videoWrap.getBoundingClientRect().width,
			// }, '<');

			// Play button animation.
			gsap.to(playIcon, {
				opacity: 1,
				scrollTrigger: {
					trigger: box,
					endTrigger: frame,
					start: isDesktop ? 'top top' : 'center center',
					end: isDesktop ? 'top center' : 'center center',
					scrub: 1,
					onUpdate: self => {
						if (self.progress > 0.5) {
							framePlay.classList.remove('disabled');
						} else {
							framePlay.classList.add('disabled');
						}
					},
					onScrubComplete: pauseVideo,
					onEnterBack: interruptVideo,
				},
			});

			// Hook that snaps video scale animation to end.
			context.add('videoTlEnd', () => {
				// videoScaleTl.scrollTrigger.scroll(videoScaleTl.scrollTrigger.labelToScroll('end'));
				// console.log( videoScaleTl.scrollTrigger.labelToScroll('end'));
				// smoothly scroll to end
				gsap.to(window, {
					duration: 1,
					scrollTo: videoScaleTl.scrollTrigger.labelToScroll('end'),
				});
			});
		});

		/* -------------------------- */
		// Standalone scroll triggers.
		/* -------------------------- */

		// Interrupt video on scroll.
		ScrollTrigger.create({
			trigger: '.v-not-hero',
			start: 'top top+=5%',
			end: '+=1px',
			onLeave: pauseVideo,
			onEnterBack: () => document.dispatchEvent(new CustomEvent('vHpUnmutePlayer')),
		});

		// Hide video on scroll.
		ScrollTrigger.create({
			trigger: '.v-not-hero',
			start: 'top top',
			end: '+=1px',
			onEnter: interruptVideo,
			onLeave: () => {
				// videoWrap.classList.add('hidden');
				pauseVideo();
			},
			onEnterBack: () => {
				videoWrap.classList.remove('hidden');
				box.classList.remove('hidden');
				document.dispatchEvent(new CustomEvent('vHpUnmutePlayer'));
			},
		});

		// Pin background gradient.
		ScrollTrigger.create({
			trigger: hpHero,
			start: 'top top',
			end: 'bottom bottom',
			scrub: 1,
			pin: bg,
		});
	});

	const onFullScreenChange = () => {
		var fullscreenElement =
			document.fullscreenElement ||
			document.mozFullScreenElement ||
			document.webkitFullscreenElement;

		if(fullscreenElement === null) {
			frameSpacer.classList.add('hidden');
			interruptVideo();
		}
	}

	document.addEventListener("fullscreenchange", onFullScreenChange, false);
	document.addEventListener("webkitfullscreenchange", onFullScreenChange, false);
	document.addEventListener("mozfullscreenchange", onFullScreenChange, false);

	[boxPlay, framePlay, box].forEach((btn) => {
		const playHeroVideo = () => {
			// Hide player
			const vimeo = frame.querySelector('iframe');

			const tl = gsap.timeline({
				defaults: {
					ease: 'ease.out',
				},
				onComplete: () => {
					player?.play();

					// Play video in full screen mode
					frameSpacer.classList.remove('hidden');
					if(window.innerWidth <= 767) {
						const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
						if(ios) {
							box.classList.add('hidden');
							if(vimeo.requestFullscreen) {
								vimeo.requestFullscreen();
							} else if (vimeo.webkitRequestFullscreen) {
								vimeo.webkitRequestFullscreen();
							}
						}
						else {
							if(vimeo.requestFullscreen) {
								vimeo.requestFullscreen();
							} else if (vimeo.webkitRequestFullscreen) {
								vimeo.webkitRequestFullscreen();
							} else if (vimeo.mozRequestFullScreen) {
								vimeo.mozRequestFullScreen();
							} else if (vimeo.msRequestFullscreen)  {
								vimeo.msRequestFullscreen();
							}
						}
					}

					vimeo.classList.add('show');
					hpHero.classList.add('v-hp-hero-anniversary--playing');
					gsap.set(videoWrap, { clearProps: 'opacity' });
					gsap.set(framePlay, { clearProps: 'all' });
					gsap.set(vimeo, { clearProps: 'all' });
				},
			});

			tl.to(videoWrap, { opacity: 0 }).to(vimeo, { opacity: 1 }, '<');
		};

		btn.addEventListener('click', (e) => {
			e.preventDefault();

			const needsInit = btn.classList.contains('v-hp-hero-anniversary__play-button--box') && !init;

			if (needsInit) {
				document.addEventListener('vHpPlayerReady', playHeroVideo);
			}

			// Snap tweens to end
			gsapCtx.videoTlEnd();

			if (!needsInit) {
				playHeroVideo();
				// document.dispatchEvent(new CustomEvent('vHpUnmutePlayer'));
				player?.setMuted(false);
			}
		});
	});

	playBoxAnimation();
};

export default vHpHeroAnniversary;
